import classNames from 'classnames'
import { observer } from 'mobx-react-lite'
import { useEffect, useMemo, useState } from 'react'
import { Select } from 'shared/ui/Select/Select'
import { Button, EnumDropdownItemVariant, type IDropdownItem } from 'shared/ui'
import { InboxCardContent } from 'entities/Inbox'
import type { Inbox } from 'entities/Inbox/model/Inbox'
import { useEntitiesContext } from 'entities/EntitiesExport'
import type { IFilterValue, IWidgetProps } from 'widgets/FilterEditor/types/types'

import { WidgetMultiInboxesTrigger } from './WidgetMultiInboxesTrigger'
import styles from './styles.module.scss'

export const WidgetMultiInboxes = observer(
  ({
    filterItem,
    expandByDefault = false,
    parentsDropdownId,
  }: IWidgetProps<IFilterValue<number>>) => {
    const { inboxesStore } = useEntitiesContext()
    const [showDropdown, setShowDropdown] = useState(false)
    const [showValuesWarning, setShowValuesWarning] = useState(false)
    const filterConfig = filterItem.config
    const values = filterConfig.values ?? []
    const payloadValue = (
      Array.isArray(filterItem.payload.value) ? filterItem.payload.value : []
    ).map((id) => Number(id))
    const [primaryKeys, setPrimaryKeys] = useState<number[]>([])
    const [selectedKeys, setSelectedKeys] = useState(payloadValue)
    const inboxes = values.reduce<Inbox[]>((state, current) => {
      const key = current.key
      const inbox = inboxesStore.getInboxByNumber(key)
      if (inbox) state.push(inbox)
      return state
    }, [])
    const selectedInboxes = inboxes.filter((inbox) => selectedKeys.includes(inbox.numberId))
    const dropdownItems: IDropdownItem[] = useMemo(
      () =>
        inboxes
          .reduce<[Inbox[], Inbox[]]>(
            (values, value) => {
              const isSelected = primaryKeys.includes(value.numberId)

              if (isSelected) values[0].push(value)
              else values[1].push(value)

              return values
            },
            [[], []]
          )
          .flat()
          .map((inbox, index) => {
            return {
              id: inbox.numberId,
              label: inbox.name,
              labelContent: <InboxCardContent item={inbox} />,
              variant: EnumDropdownItemVariant.Checkbox,
              activeValue: selectedKeys.includes(inbox.numberId),
              divider: index === primaryKeys.length - 1 && primaryKeys.length !== values.length,
            }
          }),
      [selectedKeys, primaryKeys]
    )

    useEffect(() => {
      setPrimaryKeys(payloadValue)
    }, [])

    useEffect(() => {
      setTimeout(() => setShowDropdown(expandByDefault), 100)
    }, [expandByDefault])

    return (
      <Select
        text={filterItem.operator?.key ?? ''}
        type='multi'
        items={dropdownItems}
        dropdownProps={{
          ariaLabel: 'Select',
          parentsDropdownId: parentsDropdownId,
          show: showDropdown,
          textFieldProps: {
            InputProps: { placeholder: `Search ${filterItem.config.name}` },
          },
          bottomActions: selectedKeys.length
            ? () => (
                <Button
                  text='Clear'
                  contained='secondary'
                  typeBtn='text'
                  onClick={() => {
                    setPrimaryKeys([])
                    setSelectedKeys([])
                    filterItem.payload.updateHard({ value: [] })
                  }}
                />
              )
            : undefined,
          className: styles.operatorDropdown,
          withSearch: true,
          onClose: () => {
            setPrimaryKeys(selectedKeys)
            setShowValuesWarning(!selectedKeys.length)
          },
          triggerComponent: () => (
            <div
              className={classNames(styles.selectTriggerComponent, {
                [styles.warning]: showValuesWarning,
              })}
            >
              <WidgetMultiInboxesTrigger items={selectedInboxes} />
            </div>
          ),
          onChange: (item) => {
            const id = item.id as number
            const keys = item.activeValue
              ? selectedKeys.filter((key) => key !== id)
              : [...selectedKeys, id]

            setShowValuesWarning(false)
            setSelectedKeys(keys)
            filterItem.payload.updateHard({ value: keys.map((key) => String(key)) })
          },
        }}
      />
    )
  }
)
